/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import _ from '@/underscore'

import DisposableView from '@/io.ox/backbone/views/disposable'
import ext from '@/io.ox/core/extensions'
import { manifestManager } from '@/io.ox/core/manifests'
import * as util from '@/io.ox/backbone/views/actions/util'
import gt from 'gettext'

//
// Action dropdown
//
// options:
// - caret (bool; default true): show caret
// - point (string): extension point id to render items
// - title (string): dropdown title
// - simple (bool; default false): defines whether simple collection checks should be used, i.e. no folder-specific stuff
// - backdrop (bool: default false): use backdrop to capture clicks
// - checkmarkFn (function): when provided, a check mark design is used for the entry - the callback return value determines the state

const ActionDropdownView = DisposableView.extend({

  events: {
    'shown.bs.dropdown': 'onShown',
    'hidden.bs.dropdown': 'onHide'
  },

  // we use the constructor here not to collide with initialize()
  constructor: function (options) {
    // add central extension point
    this.options = { caret: true, point: this.point, title: '', simple: false, backdrop: false, ...options }
    if (!this.options.point) console.error('Missing extension point definition')
    DisposableView.prototype.constructor.apply(this, arguments)
    util.renderDropdown(this.$el, null, this.options)
    this.$toggle = this.$('.dropdown-toggle')
    this.$menu = this.$('.dropdown-menu')
    this.$ul = this.$menu
    if (this.options.data) this.setData(this.options.data)
    if (this.options.backdrop || _.device('smartphone')) {
      util.addBackdrop(this.$el)
    } else {
      // listen for click event directly on menu for proper backdrop support
      util.bindActionEvent(this.$menu)
    }
  },

  render (baton) {
    this.$menu.addClass('invisible')
    util.renderDropdownItems(this.$el, baton, this.options).done(this.finalizeRender.bind(this))
    manifestManager.loadPluginsFor('dropdown').then(() => {
      // view might be disposed already (redraw triggered, selection changed etc)
      if (this.disposed) return
      ext.point('io.ox/core/dropdown').invoke('draw', this, ext.Baton({ dropdownTitle: gt('Actions') }))
    })
    this.trigger('rendered')
    return this
  },

  finalizeRender () {
    if (this.disposed) return
    this.$menu.removeClass('invisible')
    this.trigger('ready')
  },

  onShown () {
    ext.point('io.ox/core/dropdown').invoke('show', { $ul: this.$menu }, ext.Baton({}))
  },

  onHide () {
    ext.point('io.ox/core/dropdown').invoke('hide', { $ul: this.$menu }, ext.Baton({}))
  },

  hasActions () {
    return util.hasActions(this.$el)
  },

  close () {
    this.$toggle.trigger('click')
    this.trigger('close')
  },

  updatePoint (newPoint) {
    if (!newPoint) return
    this.options.point = newPoint
  },

  // selection is expected to be array of object
  // - object must provide id and folder_id
  // - object_permissions if available
  setSelection: util.setSelection,

  setData: util.setData
})

export default ActionDropdownView
